import * as ToastPrimitive from '@radix-ui/react-toast';
import { create } from 'zustand';
import { useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  title?: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastStore {
  toasts: ToastItem[];
  push: (t: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (t) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** 便捷调用 */
export const toast = {
  success: (description: string, title?: string) =>
    useToastStore.getState().push({ description, title, variant: 'success', duration: 4000 }),
  error: (description: string, title?: string) =>
    useToastStore.getState().push({ description, title, variant: 'error', duration: 6000 }),
  info: (description: string, title?: string) =>
    useToastStore.getState().push({ description, title, variant: 'info', duration: 4000 }),
  warning: (description: string, title?: string) =>
    useToastStore.getState().push({ description, title, variant: 'warning', duration: 6000 }),
};

const VARIANT_CONFIG: Record<ToastVariant, { icon: typeof CheckCircle2; color: string; bar: string }> = {
  success: { icon: CheckCircle2, color: 'text-success', bar: 'bg-success' },
  error: { icon: AlertCircle, color: 'text-danger', bar: 'bg-danger' },
  info: { icon: Info, color: 'text-info', bar: 'bg-info' },
  warning: { icon: AlertTriangle, color: 'text-warning', bar: 'bg-warning' },
};

function ToastCard({ item }: { item: ToastItem }) {
  const dismiss = useToastStore((s) => s.dismiss);
  const config = VARIANT_CONFIG[item.variant];
  const Icon = config.icon;
  return (
    <ToastPrimitive.Root
      duration={item.duration ?? 4000}
      onOpenChange={(open) => {
        if (!open) dismiss(item.id);
      }}
      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-xl bg-bg-surface p-4 shadow-lg ring-1 ring-black/5 animate-slide-in-right data-[state=closed]:animate-fade-in"
    >
      <Icon className={`h-5 w-5 ${config.color}`} aria-hidden />
      <div className="flex flex-col gap-0.5">
        {item.title && <div className="text-sm font-medium text-text-primary">{item.title}</div>}
        {item.description && (
          <div className="text-sm text-text-secondary">{item.description}</div>
        )}
      </div>
      {item.action && (
        <button
          type="button"
          onClick={item.action.onClick}
          className="whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium text-accent transition-colors hover:bg-accent-50"
        >
          {item.action.label}
        </button>
      )}
      <ToastPrimitive.Close
        className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle hover:text-text-primary"
        aria-label="关闭"
      >
        <X className="h-3.5 w-3.5" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);

  // 自动清除 DOM 中已关闭的 toast
  useEffect(() => {
    if (toasts.length === 0) return;
  }, [toasts]);

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={4000}>
      {toasts.map((t) => (
        <ToastCard key={t.id} item={t} />
      ))}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 flex w-[380px] max-w-[calc(100vw-32px)] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  );
}
